/**
 * The client half of the seam: records, refusals and the ladder vocabulary (wf-12 U-23).
 *
 * WHY THIS IS A SEPARATE FILE FROM `records.ts`. Everything in `records.ts` is a deployment, a
 * push or a definition — rows that arrive because a relay sent them, and the console only ever
 * reads. A client is the other kind of record entirely: it is created BEFORE any Azure resource
 * exists (c-fleet-console.md), it is the identity everything else hangs off (D42), and the
 * console WRITES it. Keeping it here means the read-only fleet shapes and the one object this
 * console owns outright do not have to share a file and pretend to be the same thing.
 *
 * THE TWO AXES ARE TWO TYPES, and that is the single most important line in this file.
 * `CONSOLES.md` §2.3: `status` is technical and the system moves it from a check that passed;
 * `lifecycleStage` is commercial and a human moves it from what happened in a meeting. A client
 * can be WON commercially and `prospect` technically, which is every signed deal before its
 * install. They are declared as two unrelated unions so that no function can accidentally take
 * one where the other belongs, and so that a screen cannot merge them into one progress bar —
 * which §2.3 names as the most common way to get this screen wrong.
 *
 * WHERE THESE SHAPES COME FROM, and it is an assumption rather than a contract. `console-api`
 * publishes no OpenAPI document (recorded as a finding by `L2-console-seam`, and the reason
 * `L2-api-contract-console` exists), so these are transcribed from `console_api/schemas.py` and
 * `console_api/domain/clients.py`. Transcribed in ONE place, like `CONSOLE_API_PATHS`, so that
 * when the contract lands this file is what a generator replaces.
 */
import { FleetApiError } from './errors.ts'
import type { RelayAck } from './records.ts'

/**
 * The technical status, COMPUTED by console-api on every read (nth-ai/fleet-api#202,
 * `CONSOLES.md` §2.3), first row that applies: `offboarded` and `on-hold` are an operator's
 * recorded acts; the rest follow from the client's installs, activation codes and deployments.
 * Nobody sets it, and nothing in this console works it out: it is rendered as served.
 */
export const CLIENT_STATUSES = [
  'prospect',
  'ready',
  'installing',
  'activating',
  'live',
  'quiet',
  'ended',
  'on-hold',
  'offboarded',
] as const

export type ClientStatus = (typeof CLIENT_STATUSES)[number]

/** How many of the client's installs and deployments are in each lifecycle state. */
export interface InstallCounts {
  planned: number
  activating: number
  running: number
  quiet: number
  ended: number
}

/** A hold or an offboarding: who recorded it, when, and why. */
export interface ClientStanding {
  at: string
  by: string
  reason: string
}

/**
 * The shape check `console_api`'s `OWNING_FDE_SHAPE` applies, transcribed like every other shape
 * in this file.
 *
 * Deliberately no stricter than the server's. A form refusing an address `console-api` accepts
 * would be inventing a rule; one accepting an address it refuses turns a preventable mistake into
 * a round trip. It catches the typo class -- `melanie`, `melanie@nthai`, `Melanie Someone` -- and
 * claims nothing more: it cannot tell you the address belongs to a real person, and there is no
 * FDE registry anywhere in the fleet plane to check one against.
 */
export const OWNING_FDE_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/**
 * An Entra tenant id: a GUID, any case, no braces. The server's pattern, transcribed, and no
 * stricter, for the same reason as `OWNING_FDE_SHAPE` above. It says the value is shaped like a
 * tenant id; nothing on our side can say it is THEIR tenant, because nothing here talks to one.
 */
export const TENANT_ID_SHAPE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

/**
 * The name prefix an enrollment bundle carries: 2 to 13 characters, a lower case letter first,
 * then lower case letters and digits. The server's rule (`^[a-z][a-z0-9]*$`, length 2 to 13),
 * transcribed so the form says so before the round trip rather than after it.
 */
export const NAME_PREFIX_SHAPE = /^[a-z][a-z0-9]{1,12}$/

/** The prefix a bundle is issued with when the operator does not change it. */
export const DEFAULT_NAME_PREFIX = 'nexus'

/**
 * The six-character suffix. The SERVER generates it at issue time; this shape is only for the
 * legacy install package form, where a deployment from before naming was stored has
 * to be told the suffix its resources were already built with.
 */
export const NAME_SUFFIX_SHAPE = /^[a-z0-9]{6}$/

/**
 * The LEGACY install package form, where the prefix and environment are bounded by length and
 * nothing else. Old installs were generated from a free-text form, so their resources may carry a
 * prefix the strict `NAME_PREFIX_SHAPE` refuses (an upper case letter, a leading digit), and the
 * package for one of them has to reproduce the name it was actually built with. The server
 * accepts exactly these bounds on that path; `NAME_PREFIX_SHAPE` still governs a new bundle.
 */
export const LEGACY_NAME_PREFIX_LENGTH = { min: 2, max: 13 } as const
export const LEGACY_ENVIRONMENT_LENGTH = { min: 1, max: 8 } as const

function withinLength(value: string, bounds: { min: number; max: number }): boolean {
  return value.length >= bounds.min && value.length <= bounds.max
}

export function legacyPrefixOk(value: string): boolean {
  return withinLength(value, LEGACY_NAME_PREFIX_LENGTH)
}

export function legacyEnvironmentOk(value: string): boolean {
  return withinLength(value, LEGACY_ENVIRONMENT_LENGTH)
}

/**
 * How a deployment gets into the customer's subscription (`InstallOut.kind`,
 * `DeploymentOut.installKind`).
 *
 * `customer-deploy` is the install package: we fix the naming, the server mints the suffix, and
 * the customer runs what we hand over. `managed-application` (D50) is the Service Catalog or
 * Azure Marketplace managed application: the customer's Portal form chose the naming, we mint
 * nothing, and the publisher authorization is how it is maintained afterwards, so neither the
 * install package nor the upgrade package exists for it (console-api refuses both with 422).
 */
export const INSTALL_KINDS = ['customer-deploy', 'managed-application'] as const

export type InstallKind = (typeof INSTALL_KINDS)[number]

/** What the server assumes when a create sends no `kind`, and what an older record means. */
export const DEFAULT_INSTALL_KIND: InstallKind = 'customer-deploy'

export const INSTALL_KIND_LABELS: Record<InstallKind, string> = {
  'customer-deploy': 'Customer deploy',
  'managed-application': 'Managed application',
}

/**
 * Who applies an update to a managed application, by the Partner Center plan it was bought under
 * (`DeploymentOut.marketplacePlan`, nexus-managed-app#64).
 *
 * Every plan follows one path (nth-ai/fleet-api#187): the Fleet offers a release (Offer update, which
 * sets the target release) and the customer's administrator applies it from their own console.
 * `single-rg`: they apply it with the install's updater identity. `managed-rg`: they agree to it, and the Fleet
 * asks their approver for a JIT window and applies it inside it (nth-ai/fleet-api#213). console-api derives the kind from the plan id in one place, so this console
 * never maps an id itself; a plan it does not know arrives as `null`, as does a deployment whose
 * Relay reported none.
 */
export const MARKETPLACE_PLANS = ['single-rg', 'managed-rg'] as const

export type MarketplacePlan = (typeof MARKETPLACE_PLANS)[number]

export const MARKETPLACE_PLAN_LABELS: Record<MarketplacePlan, string> = {
  'single-rg': 'Single RG',
  'managed-rg': 'Managed RG',
}

/** A plan kind off the wire, or `null` for anything else, absence included. */
export function marketplacePlanOf(value: string | null | undefined): MarketplacePlan | null {
  return (MARKETPLACE_PLANS as readonly (string | null | undefined)[]).includes(value)
    ? (value as MarketplacePlan)
    : null
}

/**
 * True for a managed application, from an install (`kind`) or a deployment (`installKind`).
 *
 * Compared against the one value that changes behaviour rather than the default, so a record
 * from a console-api that predates the field (no key at all) reads as `customer-deploy`, which is
 * what the contract defaults it to.
 */
export function isManagedApplication(row: { kind?: string | null } | { installKind?: string | null }): boolean {
  const kind = 'installKind' in row ? row.installKind : 'kind' in row ? row.kind : undefined
  return kind === 'managed-application'
}

/** Why a managed application row offers no package. Said on the row and in the panel. */
export const MANAGED_APPLICATION_NO_PACKAGE =
  'Managed applications are installed from the Marketplace or Service Catalog and maintained ' +
  'through the publisher authorization, so there is no install, upgrade or uninstall package to hand over.'

/**
 * Which product a deployment or a pending deployment installs (`InstallOut.product`,
 * `DeploymentOut.product`).
 *
 * `nexus-prod` is what every package is issued for now, from a nexus-managed-app release's Bicep
 * package (nexus-managed-app#155): the web app, the agents worker and the Relay, activated by a
 * super admin pasting the token on the install's own `/admin/fleet` page. A managed application is
 * recorded as `nexus-prod` too: it runs the same release line, and both are offered
 * managed-application releases. `nexus-client-tenant` is the older install with a Relay, an API
 * and a console, which the Fleet offers no release any more (nth-ai/nexus-relay#13). It is no
 * longer packaged: console-api serves it the uninstall package and refuses the install and upgrade
 * packages with a 409.
 */
export const PRODUCTS = ['nexus-prod', 'nexus-client-tenant'] as const

export type Product = (typeof PRODUCTS)[number]

/**
 * What a record with no `product` key means. A console-api that predates the field sends none,
 * and every pending deployment added from now on is nexus-prod, so absence is read as that and
 * the server's 409 stays the backstop for a legacy row that arrives unmarked.
 */
export const DEFAULT_PRODUCT: Product = 'nexus-prod'

/**
 * True for a deployment or pending deployment installed from nexus-client-tenant. Compared
 * against the one value that changes behaviour, as `isManagedApplication` is, so a missing or
 * unknown value reads as the default.
 */
export function isLegacyProduct(row: { product?: string | null }): boolean {
  return row.product === 'nexus-client-tenant'
}

/** Why a legacy row offers only the uninstall package. Said on the row and in the panel. */
export const LEGACY_PRODUCT_UNINSTALL_ONLY =
  'This deployment was installed from nexus-client-tenant, which is no longer packaged. ' +
  'It can only be uninstalled: use its Uninstall package, then add a new deployment to install nexus-prod.'

/**
 * The message a package panel shows for a failed request. A 409 from the install or upgrade
 * routes is console-api saying the row is a legacy deployment that can only be uninstalled,
 * which reaches a panel when the row arrived without its `product`; it is said plainly here
 * rather than as whatever wording the server used.
 */
export function packageErrorMessage(err: unknown): string {
  if (err instanceof FleetApiError && err.status === 409) return LEGACY_PRODUCT_UNINSTALL_ONLY
  return err instanceof Error ? err.message : String(err)
}

/**
 * The environment a managed application's Portal form chose (its `environment` output): a lower
 * case letter, then up to seven lower case letters and digits. The server's rule, transcribed.
 */
const NAMING_ENVIRONMENT_SHAPE = /^[a-z][a-z0-9]{0,7}$/

/** A managed application's naming as typed into Add deployment, trimmed. */
export interface ManagedNaming {
  namePrefix: string
  namingEnvironment: string
  nameSuffix: string
}

/**
 * What is wrong with a managed application's naming, or `null` when it can be sent.
 *
 * ALL OR NONE, as console-api holds it. None is the normal case: the naming is taken at activation
 * from what the install's Relay reports, which also replaces any typed here. All three, copied
 * from the managed application's outputs, are a fallback for a Relay too old to report. Two of
 * three is neither, and the server would refuse it with a 422 on the missing field, so the form
 * says so first. Each value is then held to the server's pattern.
 */
export function managedNamingProblem(naming: ManagedNaming): { field: keyof ManagedNaming; message: string } | null {
  const fields = ['namePrefix', 'namingEnvironment', 'nameSuffix'] as const
  const missing = fields.filter((field) => naming[field] === '')
  if (missing.length === fields.length) return null
  if (missing.length > 0) {
    return {
      field: missing[0],
      message: 'Give all three of the name prefix, environment and suffix, or leave all three empty.',
    }
  }
  if (!NAME_PREFIX_SHAPE.test(naming.namePrefix)) {
    return {
      field: 'namePrefix',
      message: 'A name prefix is 2 to 13 characters: a lower case letter, then lower case letters and digits.',
    }
  }
  if (!NAMING_ENVIRONMENT_SHAPE.test(naming.namingEnvironment)) {
    return {
      field: 'namingEnvironment',
      message: 'An environment is 1 to 8 characters: a lower case letter, then lower case letters and digits.',
    }
  }
  if (!NAME_SUFFIX_SHAPE.test(naming.nameSuffix)) {
    return { field: 'nameSuffix', message: 'A suffix is six lower case letters or digits.' }
  }
  return null
}

/** The commercial axis. A human's, from a meeting. Never inferred from `status`. */
export const LIFECYCLE_STAGES = ['DISC', 'PROP', 'WON', 'MAP', 'BUILD', 'RUN'] as const

export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number]

/** On hold or offboarded: the two statuses a person recorded rather than the system computed. */
export function isStanding(status: string): boolean {
  return status === 'on-hold' || status === 'offboarded'
}

/**
 * A client record as `console-api` returns it.
 *
 * Every field is one WE own. There is no field here that came out of a customer's data: a
 * client record is a commercial and operational row about an organisation, which is what
 * `source: 'our-record'` means on the manifest entry for these screens.
 */
export interface ClientRecord {
  id: string
  slug: string
  name: string
  /** The technical axis, computed by console-api from `installCounts`, `hold` and `offboarded`. */
  status: ClientStatus
  installCounts: InstallCounts
  /** Set while an operator has the client on hold. */
  hold: ClientStanding | null
  /** Set once an operator has offboarded the client. */
  offboarded: ClientStanding | null
  /** The commercial axis. A human moves this. `null` is "no stage recorded", not "DISC". */
  lifecycleStage: LifecycleStage | null
  /**
   * Carried into the enrollment bundle, and the record gate requires it before one is issued.
   * It lives in `commercial_refs.customerId` on our side, which is a workaround the server
   * reports as a finding; from here it is one field.
   */
  customerId: string | null
  /**
   * The customer's Entra tenant, when we know it. Optional at create because it is often not
   * known on the first call, and `null` is "not recorded", not "no tenant". When it is set the
   * install package uses it and refuses a different one; when it is not, every install package
   * request has to carry one (`InstallPackageRequest.tenantId`).
   */
  tenantId: string | null
  maxDeployments: number
  owningFde: string | null
  createdAt: string | null
  updatedAt: string | null
  /** The FDE's own copy of the wf-00b output, pasted in. `null` until something is saved. */
  readinessNote: string | null
  /** The FDE confirming the manual half of wf-00b ran against this client's tenant. */
  readinessManualStepsDone: boolean
}

/**
 * Rung 1's gate is a name, a unique slug and an OWNER.
 *
 * `owningFde` is required, and it is required by `console-api` rather than only by the form. An
 * unowned client is invisible to every FDE and visible only to an operator, forever, because
 * scope is resolved by casefold-matching `owning_fde`. That is not a state anyone chooses; it is
 * what a skippable field produces. So the server refuses to create one and the browser is a
 * convenience rather than the rule.
 *
 * NOT nullable, unlike the patch below: unassignment is a real operation on an existing client,
 * while creating something already unassigned is the accident. The domain still accepts a null
 * owner internally, which is what lets the seed keep one deliberately unowned row to demonstrate
 * scoping.
 */
export interface ClientCreate {
  slug: string
  name: string
  owningFde: string
  customerId?: string | null
  /** An Entra tenant GUID (`TENANT_ID_SHAPE`), or absent/`null` when it is not known yet. */
  tenantId?: string | null
  maxDeployments?: number
}

/**
 * A client edit. Only the keys actually present are sent, because the server distinguishes "not
 * supplied" from "set to null". There is no `status`: it is computed (nth-ai/fleet-api#202), and
 * console-api refuses one with 422. On hold and offboarded are their own calls with a reason.
 */
export interface ClientPatch {
  lifecycleStage?: LifecycleStage | null
  name?: string
  maxDeployments?: number
  customerId?: string | null
  /** `null` clears it, which the server honours for this field (unlike `customerId` above). */
  tenantId?: string | null
  owningFde?: string | null
  readinessNote?: string | null
  readinessManualStepsDone?: boolean
}

/**
 * One named reason a gate did not open.
 *
 * `field` is the thing to go and fill in; `reason` is what the check actually found. Both,
 * because "status" on its own does not tell an operator that a readiness assessment is what
 * they are waiting on. This is the shape `CONSOLES.md` §2.3 requires a screen to RENDER: a
 * gate that has not passed is never a greyed-out button.
 */
export interface MissingRequirement {
  /**
   * The MACHINE name, exactly as the server stores it, including
   * `commercial_refs.customerId` — a path into a JSONB document rather than a column, because
   * `cc_client` has none. Kept precise on purpose: tests key off it, and it stays true when the
   * column eventually lands. Never render it.
   */
  field: string
  /** The HUMAN name, and the one to put on screen. The server sends it; never derive it. */
  label: string
  reason: string
}

/** Why a bundle cannot be issued yet, in the operator's own words. */
export interface ClientReadiness {
  clientId: string
  status: ClientStatus
  ready: boolean
  missing: MissingRequirement[]
}

/**
 * What issuing a bundle returns, ONCE.
 *
 * The token is stored as a hash on our side, so there is no route that reads it back and no
 * later time at which it can be recovered. A screen showing this has one chance to put it
 * somewhere useful, which is why the reveal carries a warning BEFORE the value appears.
 */
export interface IssuedBundle {
  token: string
  clientId: string
  customerId: string
  fleetApiHostname: string
  environmentClass: string
  expiresAt: string
  singleUse: boolean
  deploymentLimit: number
  bundleId: string
  /**
   * The pending deployment this token activates, whose naming the enrolled deployment takes.
   * A token carries no naming of its own: it is generated days after the install package, and
   * regenerating one after expiry must not give the deployment a new suffix. `null` on a
   * reactivation token and on the install-less tooling path.
   */
  installId: string | null
}

/**
 * What is readable about a bundle afterwards.
 *
 * There is deliberately no `token` field, mirroring `BundleSummaryOut`: a summary type that
 * could carry the value is one refactor away from doing so.
 */
export interface BundleSummary {
  bundleId: string
  clientId: string
  environmentClass: string | null
  expiresAt: string
  /** Set when a deployment redeemed it. `null` is the weeks-long wait, which is not an error. */
  usedAt: string | null
  deploymentId: string | null
  issuedBy: string | null
  createdAt: string | null
  /** The pending deployment this token was generated for. `null` as on `IssuedBundle`. */
  installId: string | null
}

/** `InstallOut.state`. An install is `enrolled` once a relay redeemed one of its tokens. */
export type InstallState = 'pending' | 'enrolled' | 'archived'

/**
 * A deployment BEFORE its activation, as `console-api` returns it (`InstallOut`).
 *
 * WHY THIS EXISTS. The real sequence is: the operator adds a deployment, downloads its install
 * package, the customer installs over days, and only when the customer's Admin Console reaches
 * its Activation step is a 14 day token generated and pasted. So the naming cannot live on the
 * token; it lives here, fixed when the deployment is added, and every token for this install and
 * the deployment it finally enrolls take it from here. A pending install holds one of the
 * client's entitled deployment slots from the moment it exists.
 */
export interface InstallRecord {
  installId: string
  clientId: string
  environmentClass: string
  kind: InstallKind
  /**
   * The naming, nullable since managed applications (D50). A `customer-deploy` install always
   * has a prefix and a server-minted suffix (six of `[a-z0-9]`), and no `namingEnvironment`: its
   * environment is the class. A `managed-application` has all three as its outputs reported them,
   * or none until activation fills them from what its Relay reports.
   */
  namePrefix: string | null
  namingEnvironment: string | null
  nameSuffix: string | null
  state: InstallState
  /** Set at enrollment. The install is then represented by that deployment's row. */
  deploymentId: string | null
  createdAt: string | null
  createdBy: string | null
  archivedAt: string | null
  /**
   * Which product the install is for. Optional because this record is read off the wire with no
   * mapper and a console-api that predates the field sends no key: read it through
   * `isLegacyProduct`, which takes absence as `DEFAULT_PRODUCT`.
   */
  product?: Product
}

/**
 * Add deployment. Everything optional; the server defaults to `prod`, `customer-deploy` and, for
 * that kind, the prefix `nexus`.
 *
 * `namingEnvironment` and `nameSuffix` belong to a managed application only, and are sent with its
 * `namePrefix` all together or not at all. A `customer-deploy` create that carries either is
 * refused (422): its suffix is the server's and its environment is the class.
 */
export interface InstallCreate {
  environmentClass?: string
  kind?: InstallKind
  namePrefix?: string | null
  namingEnvironment?: string | null
  nameSuffix?: string | null
}

/**
 * What an install's token looks like right now, derived from the bundles issued for it.
 *
 * `none` is an install whose customer has not reached Activation yet, which is the normal state
 * for days. `expired` is not a fault either: the customer was not ready inside 14 days, and the
 * answer is to generate another, which keeps the naming because the naming is the install's.
 */
export type InstallTokenStatus =
  | { kind: 'none' }
  | { kind: 'live'; expiresAt: string }
  | { kind: 'expired'; expiresAt: string }

export function installTokenStatus(installId: string, bundles: BundleSummary[], now: Date): InstallTokenStatus {
  const mine = bundles
    .filter((b) => b.installId === installId && !isRedeemed(b))
    .sort((a, b) => Date.parse(b.expiresAt) - Date.parse(a.expiresAt))
  const latest = mine[0]
  if (!latest) return { kind: 'none' }
  return isLive(latest, now)
    ? { kind: 'live', expiresAt: latest.expiresAt }
    : { kind: 'expired', expiresAt: latest.expiresAt }
}

/**
 * True when a deployment carries its own naming, so the install package reads it.
 *
 * Truthiness rather than `!== null`, deliberately: a console-api that predates naming sends no
 * key at all. `undefined` has to read as legacy too, or the panel would hide the prefix and
 * suffix inputs a legacy deployment needs.
 *
 * A managed application waiting for its naming is `false` here too, but that is NOT the legacy
 * path: it has no package at all, and the panels check `isManagedApplication` before this.
 */
export function hasStoredNaming(row: { namePrefix?: string | null; nameSuffix?: string | null }): boolean {
  return Boolean(row.namePrefix) && Boolean(row.nameSuffix)
}

/**
 * What a deployment is called on screen: `<prefix>-<env>-<suffix>` when it carries naming, else
 * its environment class.
 *
 * console-api has no separately named deployment, so the label is derived. The naming is the
 * better label wherever it exists, for two reasons: it is what the install's resource names are
 * built from, so it matches what an operator sees in the customer's portal, and the suffix tells
 * two deployments of one client apart, which the class alone cannot once both are `prod` (D39).
 * A deployment enrolled before naming was stored keeps the class, which is all there is.
 *
 * A managed application's middle part is its `namingEnvironment`, the Portal form's
 * `environment` output, which need not be the class. One with no naming yet (filled at activation
 * from what its Relay reports) says what it is rather than passing for a legacy row.
 */
export function deploymentNameOf(
  row: {
    environmentClass: string
    namePrefix?: string | null
    namingEnvironment?: string | null
    nameSuffix?: string | null
  } & ({ kind?: string | null } | { installKind?: string | null }),
): string {
  if (row.namePrefix && row.nameSuffix) {
    return `${row.namePrefix}-${row.namingEnvironment || row.environmentClass}-${row.nameSuffix}`
  }
  return isManagedApplication(row) ? `${row.environmentClass} · managed application` : row.environmentClass
}

/** True when a bundle has been redeemed by a deployment. */
export function isRedeemed(bundle: BundleSummary): boolean {
  return bundle.usedAt !== null
}

/** True when a bundle can still be redeemed: not used, and not past its expiry. */
function isLive(bundle: BundleSummary, now: Date): boolean {
  return !isRedeemed(bundle) && Date.parse(bundle.expiresAt) > now.getTime()
}

/** The console's own error codes. `console_api/errors.py`, NOT the fleet `Error` enum. */
export const CONSOLE_ERROR_CODES = [
  'unknown-field',
  'missing-field',
  'invalid-value',
  'unauthorized',
  'forbidden',
  'not-found',
  'slug-exists',
  'client-not-ready',
  'max-deployments-reached',
  'conflict',
  'too-large',
  'upstream-rejected',
  'upstream-unavailable',
] as const

export type ConsoleErrorCode = (typeof CONSOLE_ERROR_CODES)[number]

/**
 * The console error envelope. Same SHAPE as the fleet API's `Error`, a different enum, plus
 * one field the fleet envelope does not have.
 *
 * `missing` is that field, and it is the reason this type is written down rather than inferred.
 * `CONSOLES.md` §2.3 says a refusal names the missing field or the failed check, so the server
 * sends a list and the screen renders it. Assuming the fleet `Error` shape here would parse the
 * refusal successfully and silently drop the only part of it worth showing somebody.
 */
export interface ConsoleErrorBody {
  code: ConsoleErrorCode | string
  message: string
  requestId: string
  field?: string
  missing?: MissingRequirement[]
}

/**
 * A refusal that names what is missing.
 *
 * It carries `missing` as a field on the error because the screen that catches it has to
 * render the list, and a refusal whose reasons are only in a message string is a refusal a
 * screen can display and not use. `code` is kept as well: `client-not-ready` and
 * `max-deployments-reached` are both 409s with a named reason, and they are not the same
 * problem — one is waiting on an assessment, the other on an entitlement.
 */
export class ConsoleRefusal extends FleetApiError {
  readonly code: string
  readonly missing: MissingRequirement[]
  readonly field: string | null
  readonly requestId: string | null

  constructor(body: ConsoleErrorBody, status: number, url: string) {
    super(body.message, status, url)
    this.name = 'ConsoleRefusal'
    this.code = body.code
    this.missing = body.missing ?? []
    this.field = body.field ?? null
    this.requestId = body.requestId ?? null
  }
}

/**
 * What a refusal names as in the way, or the refusal itself as the one entry when it names
 * nothing, so a screen always has something to render. `label` names the refused thing.
 */
export function refusalReasons(refusal: ConsoleRefusal, label = 'This request'): MissingRequirement[] {
  return refusal.missing.length > 0
    ? refusal.missing
    : [{ field: refusal.field ?? 'request', label: refusal.field ?? label, reason: refusal.message }]
}

/**
 * Parse a `console-api` error body, or return null if it is not one.
 *
 * Deliberately strict about `missing`: an entry without both halves is dropped rather than
 * rendered as `undefined - undefined`, which is how a screen ends up showing an operator a
 * refusal that is worse than no refusal at all.
 */
export function parseConsoleError(body: unknown): ConsoleErrorBody | null {
  if (!body || typeof body !== 'object') return null
  const candidate = body as Record<string, unknown>
  if (typeof candidate.code !== 'string' || typeof candidate.message !== 'string') return null
  const missing = Array.isArray(candidate.missing)
    ? candidate.missing.filter(
        (item): item is MissingRequirement =>
          Boolean(item) &&
          typeof item === 'object' &&
          typeof (item as MissingRequirement).field === 'string' &&
          typeof (item as MissingRequirement).reason === 'string',
      )
    : undefined
  return {
    code: candidate.code,
    message: candidate.message,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : '',
    field: typeof candidate.field === 'string' ? candidate.field : undefined,
    missing,
  }
}

/**
 * The client routes `console-api` serves, in one map.
 *
 * Same device and same caveat as `CONSOLE_API_PATHS` in `http-fleet-api.ts`: transcribed by
 * hand under `/console/v1` — visibly not the Fleet API's `/fleet/v1`, which is a different
 * application on a different hostname serving deployment credentials — and asserted by nothing
 * until `console-api` publishes a contract. One map, so a contract test replaces one thing.
 */
export const CONSOLE_CLIENT_PATHS = {
  clients: '/console/v1/clients',
  client: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}`,
  // On hold and offboarded (nth-ai/fleet-api#202): PUT with a reason sets it, DELETE lifts it.
  hold: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/hold`,
  offboarding: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/offboarding`,
  telemetrySummary: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/telemetry-summary`,
  readiness: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/readiness`,
  // `enrollment-bundles`, two l's. console-api serves exactly this and has since the US English
  // rename (clients.py:202 POST, :236 GET, :253 DELETE). This map had the single-l spelling, and
  // because CONSOLE_API_PATHS re-exports it, ALL THREE bundle operations were a 404: minting a
  // bundle, listing them, and archiving one. Minting is the one that matters - it is how an FDE
  // issues the single-use token a relay enrolls with, so the console could not start an install.
  //
  // Nothing went red, because there is no contract test over console-api yet (see the caveat
  // above) and the mock in http-fleet-api.test.ts carried the same misspelling, so the client
  // agreed with itself and with nothing else.
  bundles: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/enrollment-bundles`,
  bundle: (clientId: string, bundleId: string) =>
    `/console/v1/clients/${encodeURIComponent(clientId)}/enrollment-bundles/${encodeURIComponent(bundleId)}`,
  installs: (id: string) => `/console/v1/clients/${encodeURIComponent(id)}/installs`,
  install: (clientId: string, installId: string) =>
    `/console/v1/clients/${encodeURIComponent(clientId)}/installs/${encodeURIComponent(installId)}`,
  installPackage: (id: string) =>
    `/console/v1/clients/${encodeURIComponent(id)}/install-package`,
  installPackageDownload: (id: string) =>
    `/console/v1/clients/${encodeURIComponent(id)}/install-package/download`,
  upgradePackage: (id: string) =>
    `/console/v1/clients/${encodeURIComponent(id)}/upgrade-package`,
  upgradePackageDownload: (id: string) =>
    `/console/v1/clients/${encodeURIComponent(id)}/upgrade-package/download`,
  uninstallPackageDownload: (id: string) =>
    `/console/v1/clients/${encodeURIComponent(id)}/uninstall-package/download`,
  // The nexus-managed-app releases, the one line every install runs: what a target release names
  // (nexus-managed-app#64), and, where `bicepPackage` is set, what a customer package is issued for
  // (nexus-managed-app#155). Not a client path, kept here beside the packages it is picked for.
  managedAppReleases: '/console/v1/managed-app-releases',
  // The deployments notified of one of those releases, and notifying chosen ones of it, from the
  // Releases page (nth-ai/fleet-api#187). Kept beside the list it is a release of.
  releaseNotices: (version: string) =>
    `/console/v1/managed-app-releases/${encodeURIComponent(version)}/notices`,
  // Every release's notices at once, for a page that shows them all (nth-ai/fleet-api#192).
  fleetReleaseNotices: '/console/v1/release-notices',
  // A deployment's target release (nexus-managed-app#64). A deployment path rather than a client
  // one, kept beside `managedAppReleases` because a target is one of those releases named for one
  // deployment.
  targetRelease: (deploymentId: string) =>
    `/console/v1/deployments/${encodeURIComponent(deploymentId)}/target-release`,
  // The plan an operator declares for a managed application whose Relay reported none, and the
  // updates applied to a Managed RG install at its administrator's request (nexus-managed-app#64). Deployment paths kept
  // here beside the target release, because all three are what the Actions screen does with a
  // managed application's releases.
  marketplacePlan: (deploymentId: string) =>
    `/console/v1/deployments/${encodeURIComponent(deploymentId)}/marketplace-plan`,
  managedUpdates: (deploymentId: string) =>
    `/console/v1/deployments/${encodeURIComponent(deploymentId)}/managed-updates`,
  managedUpdate: (deploymentId: string, operationId: string) =>
    `/console/v1/deployments/${encodeURIComponent(deploymentId)}/managed-updates/${encodeURIComponent(operationId)}`,
} as const

// --- install package --------------------------------------------------------

/**
 * An install package is FOR one pending install or one deployment, never for a client in general.
 *
 * The naming comes from that row on the server: prefix and suffix from the install (or the
 * deployment that inherited them), environment from its environment class. So a request normally
 * carries no naming at all, and an install cannot be generated under a name the operator did not
 * add the deployment with.
 *
 * Exactly one of `installId` and `deploymentId`. The type allows both absent only because the
 * two are equally shaped strings and a union would buy nothing the form does not already
 * guarantee; the server refuses a request with neither or both.
 *
 * The package installs nexus-prod. A row installed from nexus-client-tenant is refused with a 409
 * (`isLegacyProduct`): it gets the uninstall package and nothing else.
 */
export interface InstallPackageRequest {
  installId?: string
  deploymentId?: string
  /**
   * REQUIRED when the client has no `tenantId`, and refused (422, `invalid-value`, field
   * `tenantId`) when it differs from the client's. Omitted when the client has one.
   */
  tenantId?: string
  /**
   * LEGACY ONLY: a deployment from before naming was stored. Then `namePrefix` and
   * `nameSuffix` are required, and `environment` defaults to the row's environment class. Sent
   * against a row that DOES store naming, any of them is refused with a 422 naming the field,
   * rather than silently overriding the name the install was issued under.
   */
  namePrefix?: string
  nameSuffix?: string
  environment?: string
  location?: string
  fabricSkuName?: string
  fabricLocation?: string
  /**
   * Whether the install deploys its own Fabric capacity or points at one the customer already
   * owns. `create` is the default and is what every install did before this field existed.
   */
  fabricCapacityMode?: 'create' | 'existing'
  /**
   * BYO only, and the server refuses it alongside `create` rather than ignoring it: send `''`
   * when the mode is `create`, not the last value the form held. The full ARM resource ID,
   * `/subscriptions/<sub>/resourceGroups/<rg>/providers/Microsoft.Fabric/capacities/<name>`.
   */
  existingFabricCapacityId?: string
  /**
   * The nexus-managed-app release the package is issued for (`listManagedAppReleases`, one with
   * `bicepPackage`). Omitted for the newest such; an unknown one, or one without a Bicep package,
   * is refused 422 on `releaseVersion`.
   */
  releaseVersion?: string
}

/**
 * The generated `main.bicepparam`, ready to save (nexus-managed-app `docs/bicep-package.md`). The
 * install is one resource group deployment, so there is no separate roles parameter file.
 */
export interface InstallPackageResult {
  mainBicepparam: string
  namePrefix: string
  environment: string
  nameSuffix: string
  resourceGroupName: string
  /** The tenant the files were written for: the client's, or the one the request carried. */
  tenantId: string
  /** The release the package's images belong to. */
  releaseVersion: string
}

// --- upgrade package --------------------------------------------------------

/**
 * An upgrade package is for one deployment that is already installed, never for a pending one:
 * there is nothing yet to upgrade.
 *
 * It re-runs the release's `main.bicep` (its Bicep package, nexus-managed-app
 * `docs/bicep-package.md`) with that release's three images, and its script reads every other
 * parameter (location, the Fabric capacity, the sign-in values) back from the customer's own
 * resource group deployment, so the request carries nothing about placement. Naming and tenant
 * follow `InstallPackageRequest` exactly, legacy case included: the package carries the
 * credentials the install package derived from them.
 *
 * A release that only changes images needs no package: it is offered from Releases or a
 * deployment's Actions and applied from the tenant console. The package is for a release that
 * changes the template.
 *
 * nexus-prod only, as the install package is: console-api answers 409 for a deployment installed
 * from nexus-client-tenant, which can only be uninstalled (`isLegacyProduct`).
 */
export interface UpgradePackageRequest {
  deploymentId: string
  /** As `InstallPackageRequest.tenantId`. */
  tenantId?: string
  /** LEGACY ONLY, as on `InstallPackageRequest`. */
  namePrefix?: string
  nameSuffix?: string
  environment?: string
  /** As `InstallPackageRequest.releaseVersion`: the release the upgrade moves to. */
  releaseVersion?: string
}

export interface UpgradePackageResult {
  /**
   * `upgrade.sh` as the release's Bicep package ships it, the same in every package. The
   * package's `package.env` is what points it at this deployment.
   */
  upgradeScript: string
  /** `bicep/upgrade.overrides.json`: the three images, the registry and the `@secure()` values. */
  overridesJson: string
  /**
   * The images the upgrade moves to, each `<registry>/<repo>@sha256:...` as the release's Bicep
   * package names them for this Fleet: the web app, the worker (agents) and the Relay.
   */
  images: ManagedAppReleaseImages
  namePrefix: string
  environment: string
  nameSuffix: string
  resourceGroupName: string
  tenantId: string
  /** The release the upgrade moves to. */
  releaseVersion: string
}

// --- managed-application releases -------------------------------------------

/**
 * A managed-application release's three images, each `repo@sha256:<digest>` without a registry:
 * nexus-prod's web app (`web`) and worker (`agents`), and nexus-relay's Relay (`relay`), as
 * `RelayReleaseImages` names them (nth-ai/nexus-relay#13). From nexus-managed-app 2.0.0 a managed
 * application runs nexus-prod; a 1.x release, which named nexus-client-tenant's API and console,
 * is not collected, because the Fleet offers nexus-client-tenant nothing any more.
 */
export interface ManagedAppReleaseImages {
  web: string
  agents: string
  relay: string
}

/** The three images in the order a screen lists them. */
export const MANAGED_APP_RELEASE_IMAGES = ['web', 'agents', 'relay'] as const satisfies readonly (keyof ManagedAppReleaseImages)[]

/** Each image as a person reads it: the web app, the worker the agents image runs, the Relay. */
export const MANAGED_APP_RELEASE_IMAGE_LABELS: Record<keyof ManagedAppReleaseImages, string> = {
  web: 'Web app',
  agents: 'Worker',
  relay: 'Relay',
}

/**
 * One published nexus-managed-app release: a `v<x.y.z>` tag and the three images it was published
 * with, as console-api's `GET /managed-app-releases` lists them, newest first.
 *
 * The one release line every install runs, managed application and customer deploy alike: a
 * deployment's target release names one, and a customer package (install or upgrade) is issued
 * from one's Bicep package where `bicepPackage` says it has one (nexus-managed-app#155).
 */
export interface ManagedAppReleaseRecord {
  version: string
  /** The day it was tagged, `YYYY-MM-DD`. */
  date: string
  /** The nexus-prod and nexus-relay commits its images were built from, as the release records it. */
  source: string
  /** The first paragraph of its CHANGELOG entry. */
  note: string
  /** The registry the release was published to. Informational: a target carries digests only. */
  origin: string
  images: ManagedAppReleaseImages
  /**
   * The release's whole CHANGELOG entry, Markdown, which a notified tenant is shown. Empty for a
   * list collected before it was kept, and read as empty from a console-api that sends no key.
   */
  notes: string
  /**
   * The oldest template a Managed RG install can run to take this release as a major update, its
   * template redeployed inside the customer's JIT window (nth-ai/fleet-api#225). `null` when the
   * release publishes no major update, and is offered as an image update alone.
   */
  majorSince: string | null
  /**
   * Whether the release publishes a Bicep package for this Fleet (nexus-managed-app 1.0.2 and
   * later), so a customer package can be issued for it. Read as false from a console-api that
   * sends no key, as the contract defaults it.
   */
  bicepPackage: boolean
  /**
   * A beta, published to be verified on an install before the final release (nth-ai/fleet-api#265).
   * Offered like any other, but never the latest or a package's default. Read as false from a
   * console-api that sends no key, as the contract defaults it.
   */
  prerelease: boolean
}

/**
 * The release the console calls the latest, and a package names when it names none: the newest that
 * is not a beta, or the newest beta when every release is one, as console-api's package default
 * picks it (nth-ai/fleet-api#265). `releases` is newest first, as the list is.
 */
export function newestRelease<R extends Pick<ManagedAppReleaseRecord, 'prerelease'>>(releases: readonly R[]): R | undefined {
  return releases.find((r) => !r.prerelease) ?? releases[0]
}

/** A release's version as the pickers name it: `1.0.9 (beta)` for a beta, as GitHub titles one. */
export function releaseLabel(release: Pick<ManagedAppReleaseRecord, 'version' | 'prerelease'>): string {
  return release.prerelease ? `${release.version} (beta)` : release.version
}

// --- release notices --------------------------------------------------------

/**
 * One deployment notified of a managed-application release (nth-ai/fleet-api#187), as
 * `GET /managed-app-releases/{version}/notices` lists them and a notify answers with them.
 *
 * Every offer changes the three images and nothing else (nexus-managed-app#64), so a notice names no
 * upgrade kind. A notice outlives the target release it set, so a deployment notified of an older
 * release still shows as notified of it.
 *
 * WITHDRAWN once `withdrawnAt` is set: `DELETE /deployments/{id}/target-release` (Withdraw offer)
 * clears the target and marks the notice of the version it named. Both are null while the offer
 * stands, and offering the same release again clears them on the same notice. Offering a different
 * release leaves the old one's notice as it was.
 */
export interface ReleaseNoticeRecord {
  deploymentId: string
  notifiedAt: string
  /** Who notified it, as console-api took it from the bearer token. Never sent from here. */
  notifiedBy: string
  /** When the offer was withdrawn, or null while it stands. */
  withdrawnAt: string | null
  /** Who withdrew it, as console-api took it from the bearer token, or null while it stands. */
  withdrawnBy: string | null
}

/**
 * One notice among every release's (`GET /release-notices`), so it names its release. What a page
 * showing every release reads: one request per refresh rather than one per release
 * (nth-ai/fleet-api#192).
 */
export interface FleetReleaseNoticeRecord extends ReleaseNoticeRecord {
  version: string
}

/**
 * What notifying sends: the deployments, and whether to offer the release as a major update. Who
 * sent it is the bearer token's, and the images and notes are read by console-api from the release.
 */
export interface ReleaseNoticeSet {
  deploymentIds: string[]
  /**
   * Offer the release's template too, which the Fleet applies on Managed RG inside the customer's
   * JIT window (nth-ai/fleet-api#225). Absent or false offers the three images alone.
   */
  majorUpdate?: boolean
}

/**
 * Why console-api did not notify one of the deployments named, in the order it checks: one the user
 * cannot see or that does not exist (`not-found`), one that runs nexus-client-tenant, which the
 * Fleet offers no release any more and which needs a new installation (`not-nexus-prod`), and a
 * managed application whose plan nobody knows (`plan-unknown`). Then, for a major update
 * (nth-ai/fleet-api#225): a release that publishes none (`no-major-update`), an install that is not
 * Managed RG (`not-managed-rg`; a customer deploy takes that release from its upgrade package
 * instead), one whose template could not be read (`template-unknown`), one
 * whose template is older than the release's `majorSince` (`needs-new-installation`), and one that
 * already runs the release's template (`template-current`).
 *
 * `not-a-managed-app-install` stays in the contract's enum but is no longer produced: a customer
 * deploy is offered an image-only release like any nexus-prod install (nexus-managed-app#155).
 */
export const RELEASE_NOTICE_REFUSAL_CODES = [
  'not-found',
  'not-nexus-prod',
  'not-a-managed-app-install',
  'plan-unknown',
  'no-major-update',
  'not-managed-rg',
  'template-unknown',
  'needs-new-installation',
  'template-current',
] as const

export type ReleaseNoticeRefusalCode = (typeof RELEASE_NOTICE_REFUSAL_CODES)[number]

export interface ReleaseNoticeRefusal {
  deploymentId: string
  code: ReleaseNoticeRefusalCode
  message: string
}

/**
 * What one notify did. One refusal does not fail the rest: the deployments notified now, and
 * apart, the ones refused, each with console-api's reason.
 */
export interface ReleaseNoticeResult {
  version: string
  notified: ReleaseNoticeRecord[]
  refused: ReleaseNoticeRefusal[]
}

// --- target release ---------------------------------------------------------

/**
 * The release an operator has named for one managed application deployment (nexus-managed-app#64).
 *
 * A Single RG install has no update path of its own, so the Fleet records a target and serves it to
 * that deployment's Relay, and the tenant's own administrator applies it from their console with
 * the install's updater identity. THE FLEET NEVER APPLIES IT: this record is what we asked for, not what
 * the deployment runs, and a screen showing it must not read as the second.
 *
 * `images` are the release's three images by digest only (`sha256:...`), with no registry or
 * repository, because the tenant pulls them from wherever its install already does: the web app,
 * the worker (agents) and the Relay.
 */
/** Where an entry of a deployment's history came from (console-api `/deployments/{id}/history`). */
export type DeploymentHistoryKind =
  | 'console-action'
  | 'managed-update'
  | 'enrolment'
  | 'credential-rotation'
  | 'push'

/**
 * One thing that happened to a deployment (nth-ai/fleet-console#209), merged by console-api from
 * every table that records a part: console actions on it, managed updates, enrolment and
 * reactivation, credential rotations, pushes. A field the kind does not have is null.
 *
 * A console action is named by its route and method (`action`), never by the value it set: the
 * console audit row keeps no request body.
 */
export interface DeploymentHistoryEntry {
  at: string
  kind: DeploymentHistoryKind
  /**
   * `target-release.set`, `target-release.clear`, `marketplace-plan.declare`,
   * `marketplace-plan.clear`, `revert`, `hold.release`, `deployment.update`,
   * `managed-update.start` (a refused start), `deployment.write`, `managed-update.images`,
   * `managed-update.full`, `enrolled`, `reactivated`, `credential.rotated`, `credential.refused`,
   * `push.<outcome>`. A code this console does not know is shown as it came.
   */
  action: string
  actor: string | null
  /** The release a managed update applied, or the snapshot a push carried. */
  subject: string | null
  /** A managed update's status or a push's outcome. */
  status: string | null
  /** The HTTP status a console action or a credential rotation answered with. */
  statusCode: number | null
  /** A managed update's error, a push's reason, a console action's method and route. */
  detail: string | null
  /** The deployment it happened to. Absent from a console-api before fleet-api#186. */
  deploymentId?: string | null
}

/** One entry of the fleet's history (`GET /console/v1/history`): whose deployment, too. */
export interface FleetHistoryEntry extends DeploymentHistoryEntry {
  deploymentId: string
  clientId: string
  clientName: string
}

export interface TargetRelease {
  version: string
  /**
   * False for a major update: the release's template, which the Fleet applies on Managed RG inside
   * the customer's JIT window (nth-ai/fleet-api#225). True for the three images alone.
   */
  imageOnly: boolean
  images: ManagedAppReleaseImages
  setAt: string
  /** Who set it, as console-api took it from the bearer token. Never sent from here. */
  setBy: string
  /**
   * The release's CHANGELOG entry, copied when the target was set, which the tenant is shown.
   * `null` from a console-api that predates it.
   */
  notes: string | null
  /** The id the deployment's Relay is served this target's release plan under. */
  messageId: string
  /** How the release plan's push went, as the Relay last acked it. `null` until it acks. */
  push: RelayAck | null
}

/**
 * A deployment's target release as its row in `GET /deployments` carries it: which release, by whom
 * and when (nth-ai/fleet-api#192). The digests and notes are `getTargetRelease`'s. Enough for a page
 * listing every deployment's target, without a read per deployment.
 */
export type DeploymentTargetRelease = Pick<TargetRelease, 'version' | 'imageOnly' | 'setAt' | 'setBy'>

/**
 * A digest cut to something a person can compare by eye: `sha256:` and the first twelve hex
 * characters. A registry or repository in front of the `@` is dropped, because a target names
 * images by digest only and the rest would only make two equal digests look different.
 */
export function shortDigest(image: string): string {
  const digest = image.slice(image.indexOf('@') + 1)
  const [algorithm, hex] = digest.includes(':') ? digest.split(':', 2) : ['', digest]
  const head = hex.length > 12 ? `${hex.slice(0, 12)}…` : hex
  return algorithm ? `${algorithm}:${head}` : head
}

// --- declared Marketplace plan ----------------------------------------------

/**
 * A deployment's Marketplace plan all three ways, as `PUT /deployments/{id}/marketplace-plan`
 * answers (nexus-managed-app#64).
 *
 * Only a Relay from a 1.0.18 package or later reports its plan, so for every managed application
 * enrolled before that an operator declares the kind it was bought under. A plan the Relay REPORTED
 * always wins: `effectiveMarketplacePlan` is the reported kind when there is a report (`null` for
 * an id console-api does not map, never falling back to the declaration), else the declared one.
 * The effective plan is the one console-api acts on, and the one the Actions screen words itself by.
 */
export interface DeploymentMarketplacePlan {
  marketplacePlanId: string | null
  marketplacePlan: MarketplacePlan | null
  declaredMarketplacePlan: MarketplacePlan | null
  /** Who declared it, as console-api took it from the bearer token. Never sent from here. */
  declaredMarketplacePlanBy: string | null
  declaredMarketplacePlanAt: string | null
  effectiveMarketplacePlan: MarketplacePlan | null
}

// --- managed updates --------------------------------------------------------

/**
 * What the Fleet applies to a Managed RG install once Nth AI offers a release and the install's
 * administrator agrees to it: console-api asks the customer's approver for a JIT window and applies it
 * inside the window, as the Fleet's identity (nexus-managed-app#64, nth-ai/fleet-api#213).
 *
 * `images` moves the Relay, the worker and the web container apps, in that order, to the
 * release's three digests and changes nothing else. Every update applied now is `images`, because an
 * offer changes the three images only (nexus-managed-app#64). `full`, which redeployed the release's
 * whole compiled template into the managed resource group, is kept only to show the operations
 * recorded before that: console-api starts none.
 */
export const MANAGED_UPDATE_KINDS = ['images', 'full'] as const

export type ManagedUpdateKind = (typeof MANAGED_UPDATE_KINDS)[number]

export const MANAGED_UPDATE_KIND_LABELS: Record<ManagedUpdateKind, string> = {
  images: 'Images only',
  full: 'Full update',
}

export const MANAGED_UPDATE_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const

export type ManagedUpdateStatus = (typeof MANAGED_UPDATE_STATUSES)[number]

export const MANAGED_UPDATE_PHASES = ['access', 'image', 'what-if', 'deploy', 'verify'] as const

export type ManagedUpdatePhase = (typeof MANAGED_UPDATE_PHASES)[number]

export const MANAGED_UPDATE_STEP_STATES = ['pending', 'running', 'skipped', 'succeeded', 'failed'] as const

export type ManagedUpdateStepState = (typeof MANAGED_UPDATE_STEP_STATES)[number]

/**
 * The three components an update moves, in the order console-api moves them, named as the release's
 * images are (nth-ai/nexus-relay#13): each in the Container App nexus-managed-app deploys it as,
 * `ca-*-relay`, `ca-*-worker` (the agents image runs the worker) and `ca-*-web`.
 */
export const MANAGED_UPDATE_COMPONENTS = ['relay', 'agents', 'web'] as const

export type ManagedUpdateComponent = (typeof MANAGED_UPDATE_COMPONENTS)[number]

/**
 * Every component a step of an update can move now, in console-api's order: the three above and
 * the Data MCP, which runs the agents image in its own Container App (nexus-managed-app#152). Its
 * step moves to the worker's digest and is skipped on an install from before it, so an operation
 * recorded before it has three steps (fleet-api 237b5fc).
 */
export const MANAGED_UPDATE_STEP_COMPONENTS = ['relay', 'agents', 'data-mcp', 'web'] as const

/**
 * The components a step recorded before nexus-managed-app 2.0.0 names besides the Relay:
 * nexus-client-tenant's API and console. History only: console-api moves neither any more, and an
 * operation that names them is still listed and read.
 */
export const HISTORICAL_MANAGED_UPDATE_COMPONENTS = ['api', 'console'] as const

/** What a step's `component` can name: one an update moves now, or one from before 2.0.0. */
export type ManagedUpdateStepComponent =
  | (typeof MANAGED_UPDATE_STEP_COMPONENTS)[number]
  | (typeof HISTORICAL_MANAGED_UPDATE_COMPONENTS)[number]

/**
 * One step of an operation. An `images` operation starts with an `access` step, null `component`:
 * the JIT window console-api asked the customer's approver for, waiting, approved or ended
 * (nth-ai/fleet-api#213); one recorded before that has none. Then an `image` step per component; a `full` one
 * has `what-if` and `deploy`, whose `component` is null because they act on the whole deployment,
 * then a `verify` step per component. A step recorded before 2.0.0 names `relay`, `api` or
 * `console` (`HISTORICAL_MANAGED_UPDATE_COMPONENTS`), and reads as it was recorded.
 *
 * `revision` is the latest ready revision Azure reported when the step finished, and
 * `previousRevision` the one serving before it. On an image step that failed, the previous
 * revision is still there.
 */
export interface ManagedUpdateStep {
  component: ManagedUpdateStepComponent | null
  phase: ManagedUpdatePhase
  state: ManagedUpdateStepState
  message: string | null
  /** The Container App the step acted on, once it is known. */
  appName: string | null
  revision: string | null
  previousRevision: string | null
  startedAt: string | null
  finishedAt: string | null
}

/** One changed property path of a resource in a full update's what-if. Never its value. */
export interface WhatIfPath {
  path: string
  /** Azure's `propertyChangeType`: Create, Delete, Modify, Array or NoEffect. */
  change: string
}

export interface WhatIfChange {
  resourceType: string
  name: string
  /** Azure's `changeType`: Create, Delete, Modify, Deploy, Unsupported. */
  changeType: string
  paths: WhatIfPath[]
}

/**
 * A full update's what-if, as console-api keeps it: resource types, names, change types and
 * property paths, NEVER A VALUE. `counts` covers every resource the what-if named, NoChange and
 * Ignore included; `changes` only the ones that change. Many `Delete` paths are server defaults
 * the module does not set, so this is shown, not judged.
 */
export interface WhatIfSummary {
  counts: Record<string, number>
  changes: WhatIfChange[]
}

/**
 * One update applied to a Managed RG install, as `GET .../managed-updates/{operationId}` answers
 * while the console polls it. The administrator's agreement to the offered release queues it (the offer
 * alone queues nothing); console-api asks the customer's approver
 * for a JIT window, waits (still `queued`, its `access` step running), and applies it inside the
 * window as the Fleet's identity, in the install's managed resource group and nowhere else.
 */
export interface ManagedUpdate {
  id: string
  deploymentId: string
  kind: ManagedUpdateKind
  version: string
  status: ManagedUpdateStatus
  steps: ManagedUpdateStep[]
  /** A full update's what-if, once it has run. Always null on an `images` operation. */
  whatIf: WhatIfSummary | null
  error: string | null
  /**
   * Who queued it: the operator who offered the release, or `tenant:<user>` for a
   * nexus-client-tenant administrator who asked. Never sent from here.
   */
  requestedBy: string
  createdAt: string
  /** Touched by the process applying it; one that stops is failed as stale after five minutes. */
  heartbeatAt: string
  finishedAt: string | null
}

/** True while an operation is queued or running: console-api refuses a second one meanwhile. */
export function isUnfinished(operation: Pick<ManagedUpdate, 'status'>): boolean {
  return operation.status === 'queued' || operation.status === 'running'
}

// --- uninstall package ------------------------------------------------------

/**
 * An uninstall package is for one deployment, or one pending deployment whose install got
 * part-way: exactly one of `installId` and `deploymentId`, as on `InstallPackageRequest`.
 *
 * It carries `uninstall.sh`, `lib/common.sh`, the `README.md` and the deployment's `package.env`,
 * from the newest release's Bicep package, and NO password or key: deleting the resource group
 * needs none, so the package handed over for it holds none.
 * Naming and tenant resolve as for the other two packages, legacy case included.
 */
export interface UninstallPackageRequest {
  installId?: string
  deploymentId?: string
  /** As `InstallPackageRequest.tenantId`. */
  tenantId?: string
  /** LEGACY ONLY, as on `InstallPackageRequest`. */
  namePrefix?: string
  nameSuffix?: string
  environment?: string
}

/** The zip a package download saves as, named for the deployment the way console-api names it. */
export function packageFileName(
  kind: 'package' | 'uninstall',
  naming: { namePrefix: string; environment: string; nameSuffix: string },
): string {
  const name = `${naming.namePrefix}-${naming.environment}-${naming.nameSuffix}`
  return kind === 'uninstall' ? `nexus-uninstall-${name}.zip` : `nexus-${name}.zip`
}

/**
 * Only the fields the caller actually set, as a body to send.
 *
 * This is where wf-01 U-21 is honoured on the client side. The server distinguishes "not
 * supplied" from "set to null" and keeps the two axes apart by writing only what arrived, so a
 * patch that helpfully filled in the current `status` alongside a new `lifecycleStage` would
 * defeat that from here — the request would be indistinguishable from an operator moving both.
 * `undefined` is dropped; `null` is sent, because clearing a stage is a real edit.
 */
export function patchBody(patch: ClientPatch): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) body[key] = value
  }
  return body
}
